import React, { useState, useEffect } from 'react';
import { 
  Eye, EyeOff, Search, X, ChevronDown,
  Users, Store, CreditCard, TrendingUp,
  Calendar, Map, BarChart2, PieChart,
  Menu, ArrowUpRight, ArrowDownRight,
  Activity, DollarSign, UserPlus, Plus, QrCode,
  LayoutDashboard
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { Profile } from '../lib/supabase';
import { ProfileAvatar } from '../components/ProfileAvatar';
import { HelpdeskIcon } from '../components/HelpdeskIcon';
import { ChatModal } from '../components/ChatModal';
import { Tooltip } from '../components/Tooltip';
import { NotificationPermissionBanner } from '../components/NotificationPermissionBanner';
import { UpdateBadge } from '../components/UpdateBadge';
import { usePWAUpdate } from '../hooks/usePWAUpdate';
import { useCustomerSearch } from '../hooks/useCustomerSearch';
import QRCode from 'react-qr-code';

interface CustomerWithAvatar extends Profile {
  avatarUrl?: string;
}

interface Transaction {
  id: string;
  customer_smart_key: string;
  vendor_smart_key: string;
  amount: number;
  currency: string;
  payment_type: 'credit' | 'debit';
  remarks: string;
  created_at: string;
  customer?: Profile;
  vendor?: Profile;
}

interface TransactionTotals {
  debit: number;
  credit: number;
}

function Home() {
  const [showQuickAccessMenu, setShowQuickAccessMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerWithAvatar | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [avatarUrl, setAvatarUrl] = useState('https://images.unsplash.com/photo-1600486913747-55e5470d6f40');
  const [customers, setCustomers] = useState<CustomerWithAvatar[]>([]);
  const [todayTransactions, setTodayTransactions] = useState<Transaction[]>([]);
  const [displayedTransactions, setDisplayedTransactions] = useState<Transaction[]>([]);
  const [monthlyTotals, setMonthlyTotals] = useState<TransactionTotals>({ debit: 0, credit: 0 });
  const [totalBalance, setTotalBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showDebitAmount, setShowDebitAmount] = useState(false);
  const [showCreditAmount, setShowCreditAmount] = useState(false);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [showFabMenu, setShowFabMenu] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const navigate = useNavigate();

  const { performUpdate } = usePWAUpdate();

  const { 
    searchResults, 
    searching, 
    searchCustomers,
    syncQuickAccessToCache,
    setSearchResults 
  } = useCustomerSearch();

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(amount));
  };

  const maskAmount = (amount: number) => {
    return '••••••';
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (profile?.smart_key) {
      fetchQuickAccess();
      fetchTransactions();
      fetchMonthlyTotals();
      fetchTotalBalance();
    }
  }, [profile?.smart_key]);

  useEffect(() => {
    setDisplayedTransactions(
      showAllTransactions ? todayTransactions : todayTransactions.slice(0, 5)
    );
  }, [todayTransactions, showAllTransactions]);

  const fetchProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;
      if (!profile) throw new Error('لم يتم العثور على الملف الشخصي');

      setProfile(profile);
      setIsAdmin(profile.phone === '7331533033');
      
      if (profile.avatar_url) {
        const { data: { publicUrl } } = supabase
          .storage
          .from('avatars')
          .getPublicUrl(profile.avatar_url);
        setAvatarUrl(publicUrl);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      navigate('/login');
    }
  };

  const fetchQuickAccess = async () => {
    try {
      if (!profile?.smart_key) return;

      let query;
      if (profile.account_type === 'shop_owner') {
        const { data: relationships } = await supabase
          .from('vendor_customer_relationships')
          .select('customer_smart_key')
          .eq('vendor_smart_key', profile.smart_key);

        if (!relationships?.length) {
          setCustomers([]);
          return;
        }

        query = supabase
          .from('profiles')
          .select('*')
          .in('smart_key', relationships.map(r => r.customer_smart_key));
      } else {
        const { data: relationships } = await supabase
          .from('vendor_customer_relationships')
          .select('vendor_smart_key')
          .eq('customer_smart_key', profile.smart_key);

        if (!relationships?.length) {
          setCustomers([]);
          return;
        }

        query = supabase
          .from('profiles')
          .select('*')
          .in('smart_key', relationships.map(r => r.vendor_smart_key));
      }

      const { data: profiles, error: profilesError } = await query;

      if (profilesError) throw profilesError;

      const profilesWithAvatars = await Promise.all(
        (profiles || []).map(async (profile) => {
          if (profile.avatar_url) {
            const { data: { publicUrl } } = supabase
              .storage
              .from('avatars')
              .getPublicUrl(profile.avatar_url);
            return { ...profile, avatarUrl: publicUrl };
          }
          return profile;
        })
      );

      setCustomers(profilesWithAvatars);

      // Sync quick access customers to local cache
      if (profile?.account_type === 'shop_owner' && profilesWithAvatars.length > 0) {
        await syncQuickAccessToCache(profile, profilesWithAvatars);
      }
    } catch (err) {
      console.error('Error fetching quick access:', err);
    }
  };

  const fetchMonthlyTotals = async () => {
    try {
      if (!profile?.smart_key) return;

      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

      let query = supabase
        .from('transactions')
        .select('amount, payment_type')
        .gte('created_at', startOfMonth.toISOString())
        .lte('created_at', endOfMonth.toISOString());

      if (profile.account_type === 'shop_owner') {
        query = query.eq('vendor_smart_key', profile.smart_key);
      } else {
        query = query.eq('customer_smart_key', profile.smart_key);
      }

      const { data, error } = await query;

      if (error) throw error;

      const totals = (data || []).reduce((acc, transaction) => ({
        debit: acc.debit + (transaction.payment_type === 'debit' ? Math.abs(transaction.amount) : 0),
        credit: acc.credit + (transaction.payment_type === 'credit' ? Math.abs(transaction.amount) : 0)
      }), { debit: 0, credit: 0 });

      setMonthlyTotals(totals);
    } catch (err) {
      console.error('Error fetching monthly totals:', err);
    }
  };

  const fetchTotalBalance = async () => {
    try {
      if (!profile?.smart_key) return;

      let query = supabase
        .from('transactions')
        .select('amount, payment_type');

      if (profile.account_type === 'shop_owner') {
        query = query.eq('vendor_smart_key', profile.smart_key);
      } else {
        query = query.eq('customer_smart_key', profile.smart_key);
      }

      const { data, error } = await query;

      if (error) throw error;

      const total = (data || []).reduce((sum, transaction) => {
        if (profile.account_type === 'customer') {
          return sum + (transaction.amount * -1);
        }
        return sum + transaction.amount;
      }, 0);

      setTotalBalance(total);
    } catch (err) {
      console.error('Error fetching total balance:', err);
    }
  };

  const fetchTransactions = async () => {
    try {
      if (!profile?.smart_key) return;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let todayQuery = supabase
        .from('transactions')
        .select(`
          *,
          customer:profiles!transactions_customer_smart_key_fkey(
            username,
            avatar_url
          ),
          vendor:profiles!transactions_vendor_smart_key_fkey(
            username,
            avatar_url
          )
        `)
        .gte('created_at', today.toISOString())
        .order('created_at', { ascending: false });

      if (profile.account_type === 'shop_owner') {
        todayQuery = todayQuery.eq('vendor_smart_key', profile.smart_key);
      } else {
        todayQuery = todayQuery.eq('customer_smart_key', profile.smart_key);
      }

      const { data: todayData, error: todayError } = await todayQuery;

      if (todayError) throw todayError;

      setTodayTransactions(todayData || []);
      setDisplayedTransactions(showAllTransactions ? todayData || [] : (todayData || []).slice(0, 5));
    } catch (err) {
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerSearch = async (value: string) => {
    setSearchQuery(value);
    
    if (value.length < 2) {
      setSearchResults([]);
      return;
    }

    if (profile) {
      await searchCustomers(value, profile);
    }
  };

  const handleAddToQuickAccess = async (selectedProfile: CustomerWithAvatar) => {
    try {
      if (!profile?.smart_key) return;

      const { error } = await supabase
        .from('quick_access')
        .insert(
          profile.account_type === 'shop_owner' 
            ? {
                vendor_smart_key: profile.smart_key,
                customer_smart_key: selectedProfile.smart_key
              }
            : {
                vendor_smart_key: selectedProfile.smart_key,
                customer_smart_key: profile.smart_key
              }
        );

      if (error) {
        if (error.code === '23514') {
          alert('يمكنك إضافة 6 عناصر كحد أقصى للوصول السريع. الرجاء إزالة بعض العناصر أولاً.');
        } else {
          throw error;
        }
        return;
      }

      await fetchQuickAccess();

      setShowQuickAccessMenu(false);
      setSearchQuery('');
      setSearchResults([]);
    } catch (err) {
      console.error('Error adding to quick access:', err);
      alert('فشل في الإضافة إلى الوصول السريع');
    }
  };

  const handleRemoveFromQuickAccess = async (smartKey: string) => {
    try {
      if (!profile?.smart_key) return;

      const { error } = await supabase
        .from('quick_access')
        .delete()
        .match(
          profile.account_type === 'shop_owner'
            ? {
                vendor_smart_key: profile.smart_key,
                customer_smart_key: smartKey
              }
            : {
                vendor_smart_key: smartKey,
                customer_smart_key: profile.smart_key
              }
        );

      if (error) throw error;

      await fetchQuickAccess();
    } catch (err) {
      console.error('Error removing from quick access:', err);
      alert('فشل في إزالة العنصر من الوصول السريع');
    }
  };

  const handleProfileClick = (selectedProfile: CustomerWithAvatar) => {
    navigate(`/customer/${selectedProfile.smart_key}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white px-6 pt-8 flex items-center justify-center">
        <div className="text-gray-500">جاري التحميل...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Notification Permission Banner */}
      <NotificationPermissionBanner />
      
      <div className="px-6 pt-8 pb-4">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <div className="relative">
                <ProfileAvatar
                  src={avatarUrl}
                  username={profile?.username || ''}
                  size="sm"
                />
                <button
                  onClick={() => setShowQRCode(!showQRCode)}
                  className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center"
                >
                  <QrCode size={12} className="text-white" />
                </button>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => navigate('/update-profile')}
                  className="p-2 rounded-full hover:bg-gray-100"
                >
                  <Menu size={20} className="text-gray-600" />
                </button>
                {isAdmin && (
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="p-2 rounded-full hover:bg-gray-100 text-blue-600"
                    title="Dashboard"
                  >
                    <LayoutDashboard size={20} />
                  </button>
                )}
                {/* Update Badge */}
                <UpdateBadge onClick={performUpdate} />
              </div>
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <p className="text-sm text-gray-500">الرصيد الإجمالي</p>
                <Tooltip text="إجمالي الرصيد المستحق لك من جميع العملاء" />
              </div>
              <p className="text-xl font-bold">{formatAmount(totalBalance, profile?.currency || 'YER')} {profile?.currency}</p>
              <p className="text-xs text-gray-500 mt-1">Smart Key: {profile?.smart_key}</p>
            </div>
          </div>
          <button className="relative p-3 rounded-full bg-gradient-to-r from-blue-50 to-purple-50 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group">
            <HelpdeskIcon 
              size={24} 
              className="text-blue-600 group-hover:text-purple-600 transition-colors duration-300" 
              onClick={() => setShowChatModal(true)}
            />
          </button>
        </div>

        {showQRCode && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white w-full max-w-sm rounded-2xl p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Smart Key QR Code</h3>
                <button
                  onClick={() => setShowQRCode(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
              <div className="bg-white p-4 rounded-xl flex flex-col items-center space-y-4">
                <QRCode
                  value={profile?.smart_key || ''}
                  size={200}
                  style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                  viewBox={`0 0 256 256`}
                />
                <p className="text-sm text-gray-500">امسح الرمز للاتصال</p>
                <p className="font-mono text-sm">{profile?.smart_key}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory hide-scrollbar">
          <div className="flex-none w-[calc(100vw-48px)] sm:w-72 h-44 rounded-2xl p-6 relative bg-gradient-to-br from-blue-500 via-purple-400 to-pink-300 snap-start">
            <div className="flex justify-between items-start">
              <p className="text-white text-lg">الأجل</p>
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/2560px-Visa_Inc._logo.svg.png" alt="Visa" className="h-6 object-contain" />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <p className="text-white text-3xl font-bold">
                {showDebitAmount 
                  ? formatAmount(monthlyTotals.debit, profile?.currency || 'YER')
                  : maskAmount(monthlyTotals.debit)
                }
              </p>
              <button 
                className="text-white opacity-80 hover:opacity-100 transition-opacity"
                onClick={() => setShowDebitAmount(!showDebitAmount)}
              >
                {showDebitAmount ? (
                  <EyeOff size={20} />
                ) : (
                  <Eye size={20} />
                )}
              </button>
            </div>
            <div className="flex justify-between items-end absolute bottom-6 left-6 right-6">
              <p className="text-white opacity-80">إجمالي الأجل للشهر الحالي</p>
              <p className="text-white opacity-80">{profile?.currency}</p>
            </div>
          </div>

          <div className="flex-none w-[calc(100vw-48px)] sm:w-72 h-44 rounded-2xl p-6 relative bg-gradient-to-br from-emerald-500 via-teal-400 to-cyan-300 snap-start">
            <div className="flex justify-between items-start">
              <p className="text-white text-lg">المبالغ المحصلة</p>
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/1280px-Mastercard-logo.svg.png" alt="Mastercard" className="h-6 object-contain" />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <p className="text-white text-3xl font-bold">
                {showCreditAmount 
                  ? formatAmount(monthlyTotals.credit, profile?.currency || 'YER')
                  : maskAmount(monthlyTotals.credit)
                }
              </p>
              <button 
                className="text-white opacity-80 hover:opacity-100 transition-opacity"
                onClick={() => setShowCreditAmount(!showCreditAmount)}
              >
                {showCreditAmount ? (
                  <EyeOff size={20} />
                ) : (
                  <Eye size={20} />
                )}
              </button>
            </div>
            <div className="flex justify-between items-end absolute bottom-6 left-6 right-6">
              <p className="text-white opacity-80">إجمالي المبالغ المحصلة للشهر الحالي</p>
              <p className="text-white opacity-80">{profile?.currency}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 mt-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-2">
            <h2 className="text-lg font-semibold">
              {profile?.account_type === 'shop_owner' ? 'الوصول السريع' : 'التجار'}
            </h2>
            <Tooltip 
              text={
                profile?.account_type === 'shop_owner' 
                  ? "اضافة و حذف العملاء الى قائمة الوصول السريع، يمكنكم الضغط على ايقونة العميل لاستعراض تقرير تفصيلي على عملياته"
                  : "قائمة التجار الذين تتعامل معهم"
              }
            />
          </div>
          <button className="text-gray-400">•••</button>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory hide-scrollbar">
          {customers.length < 6 && (
            <div className="flex-none flex flex-col items-center space-y-2">
              <button 
                className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center"
                onClick={() => setShowQuickAccessMenu(true)}
              >
                <Plus size={20} className="text-blue-600" />
              </button>
              <p className="text-xs text-gray-600">جديد</p>
            </div>
          )}
          {customers.map(profile => (
            <div 
              key={profile.smart_key} 
              className="flex-none flex flex-col items-center space-y-2 relative"
            >
              <div className="relative">
                <ProfileAvatar
                  src={profile.avatarUrl}
                  username={profile.username}
                  size="lg"
                  onClick={() => handleProfileClick(profile)}
                />
                <button
                  onClick={() => handleRemoveFromQuickAccess(profile.smart_key)}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                >
                  <X size={12} className="text-white" />
                </button>
              </div>
              <p className="text-xs text-gray-600">{profile.username.split(' ')[0]}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="px-6 mt-8 pb-24">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">اليوم</h2>
          <button className="text-gray-400">•••</button>
        </div>
        <div className="space-y-4">
          {displayedTransactions.length === 0 ? (
            <p className="text-center text-gray-500 py-4">لا توجد عمليات في هذه الفترة</p>
          ) : (
            displayedTransactions.map((transaction) => (
              <div 
                key={transaction.id} 
                className="flex items-center justify-between p-4 bg-white rounded-xl hover:bg-gray-50 transition-colors relative group"
              >
                <div className="flex items-center space-x-4 flex-1 min-w-0">
                  <div className="flex-shrink-0">
                    <ProfileAvatar
                      src={profile?.account_type === 'shop_owner' 
                        ? transaction.customer?.avatar_url 
                          ? supabase.storage.from('avatars').getPublicUrl(transaction.customer.avatar_url).data.publicUrl 
                          : null
                        : transaction.vendor?.avatar_url
                          ? supabase.storage.from('avatars').getPublicUrl(transaction.vendor.avatar_url).data.publicUrl
                          : null
                      }
                      username={profile?.account_type === 'shop_owner' 
                        ? transaction.customer?.username || 'العميل'
                        : transaction.vendor?.username || 'التاجر'
                      }
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {profile?.account_type === 'shop_owner' 
                        ? transaction.customer?.username
                        : transaction.vendor?.username}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {new Date(transaction.created_at).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}
                      {transaction.remarks && ` • ${transaction.remarks}`}
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0 mr-4">
                  <p className={`font-semibold whitespace-nowrap ${
                    profile?.account_type === 'customer'
                      ? transaction.payment_type === 'debit' ? 'text-green-600' : 'text-red-600'
                      : transaction.payment_type === 'debit' ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {transaction.payment_type === 'debit' ? '-' : '+'}
                    {formatAmount(Math.abs(transaction.amount), transaction.currency)} {transaction.currency}
                  </p>
                </div>
                {transaction.remarks && (
                  <div className="absolute opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg whitespace-normal max-w-xs z-10">
                    {transaction.remarks}
                    <div className="absolute bottom-[-6px] left-1/2 -translate-x-1/2 w-3 h-3 bg-gray-800 transform rotate-45"></div>
                  </div>
                )}
              </div>
            ))
          )}
          {todayTransactions.length > 5 && (
            <button
              onClick={() => setShowAllTransactions(!showAllTransactions)}
              className="w-full py-3 text-sm text-gray-600 flex items-center justify-center space-x-2 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <span>{showAllTransactions ? 'عرض أقل' : 'عرض المزيد'}</span>
              <ChevronDown 
                size={16} 
                className={`transform transition-transform ${showAllTransactions ? 'rotate-180' : ''}`} 
              />
            </button>
          )}
        </div>
      </div>

      {profile?.account_type === 'shop_owner' && (
        <div className="fixed bottom-6 left-6">
          <div className="relative">
            {showFabMenu && (
              <div className="absolute bottom-16 left-0 space-y-3 animate-fade-in">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setShowFabMenu(false);
                      navigate('/add-customer');
                    }}
                    className="h-12 px-4 bg-blue-500 bg-opacity-90 rounded-full flex items-center space-x-2 text-white shadow-lg hover:bg-blue-600 transition-colors w-[120px] whitespace-nowrap"
                  >
                    <UserPlus size={20} />
                    <span>إضافة عميل</span>
                  </button>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setShowFabMenu(false);
                      navigate('/top-up');
                    }}
                    className="h-12 px-4 bg-green-500 bg-opacity-90 rounded-full flex items-center space-x-2 text-white shadow-lg hover:bg-green-600 transition-colors w-[120px] whitespace-nowrap"
                  >
                    <DollarSign size={20} />
                    <span>إضافة رصيد</span>
                  </button>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setShowFabMenu(false);
                      navigate('/add-transaction');
                    }}
                    className="h-12 px-4 bg-red-500 bg-opacity-90 rounded-full flex items-center space-x-2  text-white shadow-lg hover:bg-red-600 transition-colors w-[120px] whitespace-nowrap"
                  >
                    <CreditCard size={20}/>
                    <span>إضافة عملية</span>
                  </button>
                </div>
              </div>
            )}
            <button
              onClick={() => setShowFabMenu(!showFabMenu)}
              className="w-14 h-14 bg-[#F87171] bg-opacity-90 rounded-full flex items-center justify-center text-white shadow-lg hover:bg-[#EF4444] transition-colors"
            >
              <Plus size={24} className={`transform transition-transform ${showFabMenu ? 'rotate-45' : ''}`} />
            </button>
          </div>
        </div>
      )}

      {showQuickAccessMenu && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-md rounded-2xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">إضافة إلى الوصول السريع</h3>
              <button 
                onClick={() => {
                  setShowQuickAccessMenu(false);
                  setSearchQuery('');
                  setSearchResults([]);
                }}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="relative mb-4">
              <input
                type="text"
                placeholder="بحث..."
                value={searchQuery}
                onChange={(e) => handleCustomerSearch(e.target.value)}
                className="w-full px-4 py-3 pl-10 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500"
              />
              <Search className="absolute left-3 top-3.5 text-gray-400" size={20} />
              {searching && (
                <div className="absolute right-3 top-3">
                  <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>

            <div className="max-h-60 overflow-y-auto">
              {searchResults.map(profile => (
                <button
                  key={profile.smart_key}
                  onClick={() => handleAddToQuickAccess(profile)}
                  className="w-full flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-xl"
                >
                  <ProfileAvatar
                    src={profile.avatarUrl}
                    username={profile.username}
                    size="sm"
                  />
                  <span className="font-medium">{profile.username}</span>
                </button>
              ))}
              {searchQuery && !searching && searchResults.length === 0 && (
                <p className="text-center text-gray-500 py-4">لا توجد نتائج</p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Chat Modal */}
      <ChatModal 
        isOpen={showChatModal} 
        onClose={() => setShowChatModal(false)} 
        userId={profile?.id || ''}
      />
    </div>
  );
}

export default Home;